{
  "app": {
    "name": "WebSede",
    "tagline": "Your domain, your mail, your data.",
    "offline": "You are offline. Recent mail, calendar, tasks and notes are shown from this device; changes need a connection."
  },
  "nav": {
    "mail": "Mail",
    "calendar": "Calendar",
    "contacts": "Contacts",
    "tasks": "Tasks",
    "notes": "Notes",
    "drive": "Drive",
    "admin": "Admin",
    "domains": "Domains",
    "settings": "Settings",
    "security": "Security",
    "notifications": "Notifications",
    "mailSettings": "Mail",
    "devices": "Phone and apps"
  },
  "domains": {
    "searchPlaceholder": "Find your domain, for example example.com",
    "search": "Search",
    "available": "Available",
    "unavailable": "Taken",
    "premium": "Premium",
    "unsupported": "Not offered",
    "priceNote": "Registry price plus ICANN fee plus card processing. We add nothing.",
    "perYear": "per year",
    "register": "Register",
    "owned": "Your domains",
    "none": "No domains yet. Search below and register one at cost.",
    "expires": "renews {{date}}",
    "autoRenew": "auto-renew",
    "locked": "locked",
    "privacy": "WHOIS privacy",
    "orders": "Orders",
    "status": {
      "pending_registration": "registering",
      "active": "active",
      "pending_transfer": "transfer pending",
      "expired": "expired",
      "redemption": "redemption",
      "transferred_out": "transferred out",
      "cancelled": "cancelled",
      "pending_verification": "waiting for verification"
    },
    "renew": "Renew",
    "renewNow": "Renew now",
    "renewFor": "Renew {{domain}}",
    "newExpiry": "new expiry {{date}}",
    "autoRenewOn": "Auto-renew on",
    "autoRenewOff": "Auto-renew off",
    "autoRenewHint": "We charge the saved card 14 days before expiry and email a receipt.",
    "renewal": "renewal",
    "connected": "registered elsewhere",
    "connect": {
      "title": "Use a domain you already own",
      "hint": "Registered somewhere else? Connect it to use it for email here. You keep it at your current registrar and add a few DNS records there.",
      "placeholder": "yourcompany.com",
      "button": "Connect"
    },
    "noneConnect": "No domains yet. Connect one you already own below."
  },
  "roles": {
    "owner": "Owner",
    "admin": "Administrator",
    "billing": "Billing",
    "user": "Member",
    "group_manager": "Group manager"
  },
  "auth": {
    "signOut": "Sign out",
    "fields": {
      "email": "Email",
      "password": "Password",
      "passwordHint": "At least 10 characters. A short sentence works well.",
      "displayName": "Your name",
      "tenantName": "Workspace name",
      "locale": "Language"
    },
    "login": {
      "title": "Sign in",
      "subtitle": "Welcome back.",
      "submit": "Sign in",
      "noAccount": "No workspace yet?",
      "createWorkspace": "Create one",
      "or": "or",
      "passkey": "Sign in with a passkey",
      "forgot": "Forgot your password?",
      "emailHint": "Your account email, or your mailbox address."
    },
    "signup": {
      "title": "Create your workspace",
      "subtitle": "Your team, your domain, your data.",
      "tenantHint": "Usually your company or team name.",
      "submit": "Create workspace",
      "haveAccount": "Already have an account?",
      "signIn": "Sign in",
      "forDomain": "Create the workspace that will own {{domain}}. Checkout follows right after.",
      "closed": "WebSede opens soon. Sign-up is limited to early users until launch; write to support@websede.com if you would like early access.",
      "agree": "By creating a workspace you agree to the <terms>Terms of Service</terms> and the <aup>Acceptable Use Policy</aup>."
    },
    "mfa": {
      "title": "Second step",
      "subtitle": "Enter the code from your authenticator app.",
      "code": "Authenticator code",
      "recoveryCode": "Recovery code",
      "submit": "Continue",
      "useRecovery": "Use a recovery code",
      "useCode": "Use the authenticator app"
    },
    "invite": {
      "title": "Join the workspace",
      "subtitle": "You were invited to {{tenant}} as {{role}}.",
      "submit": "Create my account",
      "invalidTitle": "This invitation is not valid",
      "invalidSubtitle": "It may have expired or been revoked. Ask your administrator for a new one.",
      "agree": "By joining you agree to the <terms>Terms of Service</terms> and the <aup>Acceptable Use Policy</aup>."
    },
    "forgot": {
      "title": "Forgot your password?",
      "subtitle": "We will email you a link to choose a new one.",
      "submit": "Send reset link",
      "back": "Back to sign in",
      "sentTitle": "Check your inbox",
      "sentSubtitle": "If {{email}} belongs to an account, a reset link is on its way.",
      "sentHint": "The link works for one hour. Look in spam if it does not arrive within a few minutes."
    },
    "reset": {
      "title": "Choose a new password",
      "subtitle": "Every device will be signed out afterwards.",
      "newPassword": "New password",
      "confirmPassword": "Repeat the password",
      "mismatch": "The two passwords differ.",
      "submit": "Set password",
      "requestAgain": "Request a new link",
      "invalidTitle": "This link is not valid",
      "invalidSubtitle": "It may have expired or already been used.",
      "doneTitle": "Password changed",
      "doneSubtitle": "Sign in with your new password."
    },
    "verify": {
      "title": "Confirming your email",
      "checking": "One moment.",
      "doneTitle": "Email confirmed",
      "doneSubtitle": "Thanks, your address is verified.",
      "invalidTitle": "This link is not valid",
      "invalidSubtitle": "It may have expired or already been used. You can request a new one from the reminder in the app.",
      "continue": "Continue to your workspace"
    }
  },
  "settings": {
    "security": {
      "title": "Security",
      "totp": {
        "title": "Authenticator app",
        "description": "Add a second step to sign-in with an app such as Aegis, 1Password, Google Authenticator or Microsoft Authenticator.",
        "enable": "Set up authenticator",
        "enabled": "The authenticator is on. Every sign-in asks for a code.",
        "disable": "Turn off",
        "scan": "Scan the code with your authenticator app, then enter the 6-digit code it shows.",
        "manual": "Or enter this key manually:",
        "qrAlt": "QR code for the authenticator app",
        "firstCode": "Code from the app",
        "confirm": "Turn on",
        "recoveryTitle": "Save your recovery codes",
        "recoveryHint": "Each code works once, if you lose your phone. They will not be shown again."
      },
      "sessions": {
        "title": "Where you are signed in",
        "current": "this device",
        "unknownClient": "Unknown client",
        "lastSeen": "last active",
        "revoke": "Sign out"
      },
      "passkeys": {
        "title": "Passkeys",
        "description": "Sign in with your fingerprint, face or device PIN instead of a password. A passkey also counts as your second step.",
        "name": "Name",
        "namePlaceholder": "For example: Work laptop",
        "defaultName": "Passkey",
        "add": "Add passkey",
        "unsupported": "This browser does not support passkeys.",
        "none": "No passkeys yet.",
        "synced": "synced",
        "added": "added {{date}}",
        "lastUsed": "last used {{date}}",
        "remove": "Remove"
      },
      "password": {
        "title": "Password",
        "description": "Choose a new password. Other devices will be signed out; this one stays.",
        "current": "Current password",
        "new": "New password",
        "confirm": "Repeat the new password",
        "submit": "Change password",
        "done": "Password changed. Other devices were signed out."
      }
    },
    "team": {
      "inviteTitle": "Invite someone",
      "role": "Role",
      "invite": "Invite",
      "pendingTitle": "Pending invitations",
      "none": "No pending invitations.",
      "invitedBy": "invited by {{name}}",
      "expires": "expires {{date}}",
      "revoke": "Revoke",
      "sent": "Invitation emailed to {{email}}",
      "sentHint": "They have {{days}} days to accept. If it does not arrive, resend it or share the link yourself.",
      "notSent": "The email to {{email}} could not be sent. Share this link yourself; it works once and expires in {{days}} days.",
      "showLink": "Show link",
      "hideLink": "Hide link",
      "resend": "Resend"
    },
    "notifications": {
      "title": "New mail on this device",
      "intro": "Get a notification when mail arrives, even when the app is closed. Each browser or installed app is turned on separately.",
      "on": "On for this device",
      "off": "Off for this device",
      "turnOn": "Turn on",
      "turnOff": "Turn off",
      "sendTest": "Send a test",
      "testSent_one": "Test sent to {{count}} device.",
      "testSent_other": "Test sent to {{count}} devices.",
      "devices_one": "{{count}} device receives notifications",
      "devices_other": "{{count}} devices receive notifications",
      "privacy": "Sender and subject are encrypted end to end; the push service cannot read them.",
      "serverOff": "Notifications are not set up on this server yet.",
      "unsupported": "This browser does not support notifications.",
      "noWorker": "Notifications work in the installed app or the production build, where the service worker runs.",
      "blocked": "Notifications are blocked for this site. Allow them in the browser's site settings and try again."
    },
    "mail": {
      "title": "Writing mail",
      "signature": "Signature",
      "signatureHint": "Added below new messages and replies. Plain text.",
      "undoSend": "Undo send",
      "undoSendHint": "How long a sent message waits on the server, so you can take it back.",
      "undoOff": "Off",
      "undoSeconds_one": "{{count}} second",
      "undoSeconds_other": "{{count}} seconds",
      "swipeRight": "Right swipe",
      "swipeLeft": "Left swipe",
      "swipeHint": "On phones, swipe a message in the list left or right to act on it.",
      "swipeActions": {
        "archive": "Archive",
        "delete": "Delete",
        "read": "Mark read or unread",
        "star": "Star or unstar",
        "none": "None"
      }
    }
  },
  "errors": {
    "invalid_credentials": "Email or password is incorrect. You can use your account email or your mailbox address, with your WebSede password (not an app password).",
    "email_taken": "An account with this email already exists.",
    "weak_password": "Please choose a stronger password that does not contain your email.",
    "invalid_code": "That code is not valid. Check the time on your phone and try again.",
    "mfa_required": "Enter your second factor to continue.",
    "invite_not_found": "This invitation is not valid any more.",
    "tenant_unavailable": "This workspace is not available.",
    "forbidden": "You do not have permission to do this.",
    "totp_already_enabled": "An authenticator is already set up.",
    "request_failed": "The request failed. Please try again.",
    "passkey_invalid": "The passkey could not be verified. Try again.",
    "passkey_unknown": "This passkey is not registered here.",
    "passkey_challenge_missing": "The passkey request expired. Try again.",
    "passkey_challenge_invalid": "The passkey request expired. Try again.",
    "passkey_not_found": "That passkey no longer exists.",
    "csrf_token_invalid": "Your session needs a refresh. Reload the page and try again.",
    "csrf_rejected": "This request was blocked for safety. Reload the page and try again.",
    "token_invalid": "This link is not valid any more.",
    "token_expired": "This link has expired. Request a new one.",
    "password_breached": "This password has appeared in a data breach. Choose one you have not used anywhere else.",
    "password_unchanged": "Choose a password you have not used here.",
    "cannot_change_self": "Use your account settings for your own account.",
    "owner_only": "Only the owner can do this, and the owner cannot be changed this way.",
    "user_not_found": "That member no longer exists.",
    "account_suspended": "This account is suspended. Contact your workspace administrator.",
    "domain_not_purchasable": "One of the domains cannot be registered any more. Search again to see current availability.",
    "order_not_found": "That order does not exist.",
    "renewal_unavailable": "This domain's registrar does not offer renewals right now.",
    "domain_not_found": "That domain does not exist.",
    "invalid_term": "That renewal term is not allowed for this TLD.",
    "domain_locked": "Turn the transfer lock off first.",
    "registrar_unavailable": "The registrar for this domain is not available right now.",
    "no_mailbox": "You do not have a mailbox yet.",
    "mailbox_exists": "This member already has a mailbox.",
    "address_taken": "That address is already in use.",
    "domain_mail_not_set_up": "Set up email DNS for this domain first.",
    "email_not_found": "That message no longer exists.",
    "mailbox_not_found": "That mailbox does not exist.",
    "ai_quota_exceeded": "You have used all AI actions for this month.",
    "ai_unavailable": "The assistant is not available right now. Try again in a moment.",
    "offline": "Could not reach the server. Check your connection and try again.",
    "note_conflict": "Someone else changed this note in the meantime.",
    "note_read_only": "You can read this note but not change it.",
    "note_owner_only": "Only the owner of the note can do that.",
    "person_not_found": "That colleague was not found in this workspace.",
    "too_late": "Too late, the message has already left.",
    "message_rejected": "The mail server did not accept this message.",
    "recipients_refused": "The mail server refused every recipient, so the message was not sent. It is still in Drafts.",
    "empty_upload": "The file is empty.",
    "too_large": "The file is larger than 50 MB.",
    "subscription_required": "Your workspace is paused. Choose a plan to send mail and make changes.",
    "card_required": "Add a card in Billing to keep mailing addresses outside your workspace during the trial.",
    "trial_mailbox_limit": "The trial includes up to 5 mailboxes. Choose a plan to add more.",
    "plan_upgrade_required": "Your plan does not include this. The Team plan does.",
    "no_billing_account": "Choose a plan first.",
    "interval_not_offered": "This plan is billed yearly only.",
    "feature_disabled": "This feature is not available.",
    "signup_closed": "Sign-up opens at launch. Write to support@websede.com for early access.",
    "confirmation_mismatch": "The name does not match the workspace name.",
    "alias_limit": "This mailbox already has the maximum number of aliases.",
    "group_limit": "This workspace already has the maximum number of group addresses.",
    "invalid_members": "Choose members from this workspace's mailboxes.",
    "from_not_allowed": "You cannot send from that address.",
    "domain_in_use": "Delete the mailboxes, aliases and group addresses on this domain first.",
    "sending_paused": "Sending to outside addresses, invitations included, is paused for this workspace. Write to support@websede.com to turn it back on.",
    "complimentary": "This workspace is not billed.",
    "invite_limit": "A workspace can have up to 50 pending invitations. Revoke some before sending more.",
    "invite_rate_limited": "A workspace can send up to 30 invitations an hour. Wait a while and try again.",
    "rate_limited": "Too many requests. Wait a while and try again.",
    "wrong_password": "Your WebSede password is not correct.",
    "wrong_password_or_code": "Your WebSede password or the code is not correct."
  },
  "common": {
    "loading": "Loading",
    "retry": "Retry",
    "cancel": "Cancel",
    "save": "Save",
    "copy": "Copy",
    "copied": "Copied",
    "errorGeneric": "Something went wrong. Please try again.",
    "unavailableTitle": "Service unavailable",
    "unavailableSubtitle": "We could not reach the server. Check your connection and try again.",
    "saved": "Saved",
    "close": "Close",
    "showPassword": "Show password",
    "hidePassword": "Hide password"
  },
  "email": {
    "footer": {
      "account": "WebSede · You received this because this address belongs to a WebSede account.",
      "invite": "WebSede · You received this because {{inviter}}, an administrator of the {{tenant}} workspace, invited this address."
    },
    "verify": {
      "subject": "Confirm your email address",
      "greeting": "Hi {{name}},",
      "body": "Please confirm that this address belongs to you. The link works for {{hours}} hours.",
      "action": "Confirm email",
      "ignore": "If you did not create a WebSede account, you can ignore this message."
    },
    "reset": {
      "subject": "Reset your password",
      "greeting": "Hi {{name}},",
      "body": "Someone asked to reset the password for this account. The link works for {{minutes}} minutes and can be used once.",
      "action": "Choose a new password",
      "ignore": "If this was not you, ignore this message; your password stays as it is."
    },
    "invite": {
      "subject": "{{inviter}} invited you to {{tenant}}",
      "greeting": "Hello,",
      "body": "{{inviter}} ({{inviterEmail}}) invited you to join the {{tenant}} workspace on WebSede as {{role}}. The link works for {{days}} days and can be used once.",
      "action": "Accept invitation",
      "ignore": "If you do not know {{inviter}}, ignore this message; nothing happens without you."
    },
    "passwordChanged": {
      "subject": "Your password was changed",
      "greeting": "Hi {{name}},",
      "body": "The password of your WebSede account was changed on {{when}} UTC and every other device was signed out. If that was you, nothing else to do.",
      "action": "Not you? Reset your password now",
      "ignore": "If you did not change it, reset it immediately with the link above and contact your workspace administrator."
    },
    "mfaReset": {
      "subject": "Your second factor was reset",
      "greeting": "Hi {{name}},",
      "body": "{{actor}}, an administrator of your workspace, removed your authenticator app and passkeys and signed you out everywhere. You can sign in with your password and set them up again.",
      "action": "Set up a new second factor",
      "ignore": "If you did not ask for this, tell your workspace administrator right away."
    },
    "expiryAutoRenew": {
      "subject": "{{domain}} renews in {{days}} days",
      "greeting": "Hi {{name}},",
      "body": "{{domain}} expires on {{date}}. Auto-renew is on, so we will charge the saved card shortly before that date and extend the domain by a year. Make sure the card on file is valid.",
      "action": "Review your domains",
      "ignore": "To stop the renewal, turn auto-renew off before the charge date."
    },
    "expiryManual": {
      "subject": "{{domain}} expires in {{days}} days",
      "greeting": "Hi {{name}},",
      "body": "{{domain}} expires on {{date}} and auto-renew is off. If you keep the domain, renew it before that date; after expiry the registry charges a redemption fee and the name can be lost.",
      "action": "Renew now",
      "ignore": "If you meant to let it expire, nothing else is needed."
    },
    "renewed": {
      "subject": "{{domain}} was renewed",
      "greeting": "Hi {{name}},",
      "body": "{{domain}} was renewed automatically for {{amount}} and now expires on {{date}}. The receipt is in your orders.",
      "action": "See your domains",
      "ignore": "You get this message because auto-renew is on for this domain."
    },
    "autoRenewFailed": {
      "subject": "Action needed: {{domain}} could not be renewed",
      "greeting": "Hi {{name}},",
      "body": "We tried to renew {{domain}} automatically but the payment failed: {{reason}}. The domain expires on {{date}}. We will try again daily; you can also renew it now with a new card.",
      "action": "Renew now",
      "ignore": "If the domain should expire, turn auto-renew off to stop these messages."
    },
    "sendingPaused": {
      "subject": "Sending to outside addresses is paused",
      "greeting": "Hi {{name}},",
      "body": "We paused mail from your workspace to addresses outside it: {{reason}}. This protects your domains' reputation and everyone else's. Incoming mail keeps arriving, and in the WebSede web app you can still write to people inside your workspace. Phone and desktop mail apps cannot send anything until the pause is lifted. Reply to this message or write to {{support}}; we will look at it with you and turn sending back on.",
      "action": "Open the admin console",
      "ignore": "Common causes: an old contact list, a mailbox whose password was stolen, or messages recipients did not expect.",
      "reasonComplaints": "{{count}} spam complaints in the last 24 hours",
      "reasonBounces": "{{count}} messages to addresses that do not exist in the last 24 hours"
    }
  },
  "banner": {
    "unverified": "Confirm your email address to keep your account recoverable. We sent a link to {{email}}.",
    "resend": "Send again",
    "resent": "Sent"
  },
  "admin": {
    "console": "Admin console",
    "backToMail": "Back to Mail",
    "onlyAdmins": "Visible to owners and administrators only.",
    "auditNote": "Changes made here affect the whole workspace.",
    "nav": {
      "overview": "Overview",
      "users": "Users",
      "domains": "Domains",
      "audit": "Audit log",
      "billing": "Billing"
    },
    "tenantStatus": {
      "trial": "Trial",
      "active": "Active",
      "suspended": "Suspended",
      "closed": "Closed"
    },
    "overview": {
      "members": "Members",
      "mfa": "With authenticator",
      "pendingInvites": "Pending invitations",
      "unverified": "Unverified emails",
      "nextSteps": "Next steps",
      "inviteLink": "Invite your team and review who has a second factor",
      "domainLink": "Find a domain for your mail",
      "securityLink": "Secure your own account with a passkey",
      "mailboxes": "Mailboxes",
      "domains": "Domains",
      "pendingCount_one": "{{count}} invitation waiting",
      "pendingCount_other": "{{count}} invitations waiting",
      "domainHint": "Register a domain at cost or connect one you own",
      "securityHint": "Two-step verification and passkeys",
      "unverifiedNote_one": "{{count}} member has not confirmed their email address yet.",
      "unverifiedNote_other": "{{count}} members have not confirmed their email address yet.",
      "rename": "Rename the workspace"
    },
    "users": {
      "members": "Members",
      "user": "User",
      "security": "Security",
      "lastSignIn": "Last sign-in",
      "mfaOn": "authenticator on",
      "mfaOff": "no authenticator",
      "unverified": "email unverified",
      "actions": {
        "manage": "Manage",
        "close": "Close",
        "you": "you",
        "suspended": "suspended",
        "role": "Role",
        "transferOwnership": "Owner (transfer ownership)",
        "saveRole": "Save role",
        "suspend": "Suspend",
        "reactivate": "Reactivate",
        "signOutEverywhere": "Sign out everywhere",
        "resetMfa": "Reset second factor",
        "sendReset": "Email a password reset link",
        "confirmSuspend": "Suspend {{name}}? They will be signed out and cannot sign in until reactivated. Their mail keeps arriving.",
        "confirmResetMfa": "Remove the authenticator and passkeys of {{name}}? They will be signed out everywhere and asked to set them up again.",
        "confirmTransfer": "Transfer ownership to {{name}}? You will become an administrator.",
        "done": "Done."
      }
    },
    "audit": {
      "title": "Audit log",
      "description": "Sign-ins, credential changes, invitations and administrator actions. Kept for one year; entries cannot be edited or deleted.",
      "filterAll": "All events",
      "filterAuth": "Sign-ins and credentials",
      "filterInvites": "Invitations",
      "filterMembers": "Member management",
      "filterDns": "DNS",
      "when": "When",
      "actor": "Actor",
      "event": "Event",
      "target": "Target",
      "from": "From",
      "export": "Export CSV",
      "loadMore": "Load older events",
      "empty": "Nothing recorded yet.",
      "system": "system",
      "actions": {
        "auth.signed_in": "Signed in",
        "auth.signed_out": "Signed out",
        "auth.session_revoked": "Session ended",
        "auth.email_verified": "Email address confirmed",
        "auth.password_changed": "Password changed",
        "auth.password_reset": "Password reset via email link",
        "auth.totp_enabled": "Authenticator app turned on",
        "auth.totp_disabled": "Authenticator app turned off",
        "auth.passkey_added": "Passkey added",
        "auth.passkey_removed": "Passkey removed",
        "invite.created": "Invitation sent",
        "invite.resent": "Invitation resent",
        "invite.revoked": "Invitation revoked",
        "invite.accepted": "Invitation accepted, account created",
        "member.role_changed": "Role changed",
        "member.ownership_transferred": "Ownership transferred",
        "member.suspended": "Member suspended",
        "member.reactivated": "Member reactivated",
        "member.sessions_revoked": "Member signed out everywhere",
        "member.mfa_reset": "Member's second factor reset",
        "member.password_reset_sent": "Password reset link sent to member",
        "dns.email_setup_applied": "Email DNS records written",
        "order.created": "Order placed",
        "order.paid": "Order paid",
        "domain.registered": "Domain registered",
        "domain.registration_failed": "Domain registration failed",
        "domain.renewed": "Domain renewed",
        "domain.auto_renew_failed": "Auto-renew failed",
        "domain.auto_renew_changed": "Auto-renew changed",
        "domain.settings_changed": "Domain settings changed",
        "domain.nameservers_changed": "Nameservers changed",
        "domain.auth_code_viewed": "Transfer code revealed",
        "mailbox.created": "Mailbox created",
        "mailbox.deleted": "Mailbox deleted",
        "mailbox.password_reset": "Mailbox app password reissued",
        "domain.connected": "Domain connected",
        "domain.verified": "Domain ownership verified",
        "domain.disconnected": "Domain disconnected",
        "mailbox.alias_added": "Alias added",
        "mailbox.alias_removed": "Alias removed",
        "mail_group.created": "Group address created",
        "mail_group.updated": "Group address changed",
        "mail_group.deleted": "Group address deleted",
        "workspace.sending_paused": "Outside sending paused",
        "workspace.sending_resumed": "Outside sending resumed",
        "workspace.queued_mail_released": "Held outgoing mail released",
        "workspace.queued_mail_cancelled": "Held outgoing mail cancelled",
        "workspace.renamed": "Workspace renamed"
      },
      "filterOrders": "Orders and domains",
      "filterMail": "Mailboxes"
    },
    "mailboxes": {
      "title": "Mailboxes",
      "noDomain": "Set up email on a domain first; mailboxes live on your domains.",
      "address": "Address",
      "create": "Create mailbox",
      "secretTitle": "App password for {{address}}",
      "secretHint": "For phone and desktop mail apps (IMAP and SMTP). Shown once; issue a new one if it is lost.",
      "newPassword": "New app password",
      "delete": "Delete",
      "confirmDelete": "Delete {{address}} and everything in it? This cannot be undone."
    },
    "brand": "Admin",
    "aliases": {
      "add": "Add alias",
      "save": "Add",
      "remove": "Remove {{address}}",
      "confirmRemove": "Remove the alias {{address}}? Mail sent to it will no longer arrive."
    },
    "groups": {
      "title": "Group addresses",
      "body": "An address like team@ or sales@ that delivers to several mailboxes. Each member gets their own copy.",
      "needMailboxes": "Create a mailbox first; group addresses deliver to mailboxes.",
      "name": "Name",
      "namePlaceholder": "Sales team",
      "members": "Members",
      "create": "Create group address",
      "edit": "Edit",
      "save": "Save",
      "delete": "Delete",
      "confirmDelete": "Delete {{address}}? Mail sent to it will no longer arrive."
    }
  },
  "checkout": {
    "title": "Register a domain",
    "term": "Term",
    "years_one": "{{count}} year",
    "years_other": "{{count}} years",
    "registrant": "Registrant",
    "registrantHint": "Registries require a verified identity for the holder (NIS2). WHOIS privacy hides these details publicly; they are still on file with the registrar.",
    "firstName": "First name",
    "lastName": "Last name",
    "organization": "Organisation (optional)",
    "email": "Email",
    "phone": "Phone (international format)",
    "street": "Street",
    "city": "City",
    "postalCode": "Postal code",
    "country": "Country code",
    "state": "State or province (optional)",
    "privacy": "WHOIS privacy (free)",
    "summary": "Order",
    "wholesale": "Registry wholesale",
    "icann": "ICANN fee",
    "processing": "Card processing",
    "total": "Total",
    "pay": "Pay {{amount}}",
    "unavailable": "This domain cannot be registered right now: {{reason}}.",
    "note": "You pay exactly what the registry charges us plus the ICANN fee and card processing. Registrations are not refundable once the registry accepts them.",
    "hosted": "Payment happens on a hosted page; card details never reach WebSede."
  },
  "order": {
    "title": "Order",
    "status": {
      "pending_payment": "Awaiting payment",
      "paid": "Paid, registering domains",
      "completed": "Completed",
      "partially_failed": "Completed with errors",
      "failed": "Not completed",
      "cancelled": "Cancelled"
    },
    "cancelledNote": "Payment was cancelled. Nothing was charged and no domain was registered.",
    "waiting": "Waiting for the payment confirmation from the provider. This page refreshes on its own.",
    "resume": "Resume payment",
    "items": "Domains",
    "registered": "registered",
    "failed": "failed",
    "pending": "pending",
    "expires": "expires {{date}}",
    "manage": "Go to your domains",
    "reference": "Payment reference",
    "list": "All orders",
    "placed": "placed {{date}}"
  },
  "domain": {
    "settings": "Settings",
    "autoRenew": "Auto-renew",
    "lock": "Transfer lock",
    "lockHint": "Blocks transfers to another registrar. Turn it off only when you are moving the domain.",
    "whois": "WHOIS privacy",
    "whoisHint": "Hides the registrant details from public WHOIS. Free.",
    "email": {
      "title": "Email setup",
      "description": "One click writes MX, SPF, DKIM, DMARC, autoconfig and CalDAV records into the domain's zone on our DNS and provisions the domain on the mail platform.",
      "done": "set up {{date}}",
      "notYet": "not set up",
      "apply": "Set up email DNS",
      "reapply": "Re-apply records",
      "result": "{{created}} created, {{updated}} updated, {{unchanged}} unchanged."
    },
    "nameservers": {
      "title": "Nameservers",
      "ours": "The domain points at our DNS, so the records below are live.",
      "external": "The domain points at external nameservers; the records below are not in use until you switch back.",
      "useOurs": "Use our nameservers"
    },
    "records": {
      "title": "DNS records on our zone",
      "none": "No zone yet. Set up email or switch to our nameservers to create one."
    },
    "transfer": {
      "title": "Transfer out",
      "description": "To move this domain to another registrar you need its authorisation code. Transfers out are free.",
      "unlockFirst": "Turn the transfer lock off above to reveal the code.",
      "reveal": "Reveal authorisation code"
    },
    "verify": {
      "title": "Verify ownership",
      "description": "Add this TXT record at the company that hosts DNS for the domain (usually your registrar), then check. Changes can take a few minutes to appear.",
      "type": "Type",
      "name": "Name / host",
      "value": "Value",
      "check": "Check now",
      "notFound": "Not found yet. DNS shows no matching record at {{fqdn}}.",
      "foundOther": "DNS shows other values at {{fqdn}}: {{values}}",
      "done": "Verified {{date}}",
      "copy": "Copy",
      "copied": "Copied",
      "fullName": "Full name {{fqdn}}. Some DNS hosts want only the part before your registered domain."
    },
    "dnsCheck": {
      "title": "Email DNS records",
      "description": "Add these records at your DNS host, starting with the required ones.",
      "ready": "Mail is ready: MX, SPF and DKIM are in place.",
      "notReady": "Some required records are missing or different.",
      "recommendedMissing_one": "{{count}} recommended record missing or different",
      "recommendedMissing_other": "{{count}} recommended records missing or different",
      "check": "Check again",
      "checked": "checked {{date}}",
      "level": {
        "required": "Required",
        "recommended": "Recommended",
        "optional": "Optional"
      },
      "levelHint": {
        "required": "Without these, mail does not arrive or is not trusted.",
        "recommended": "Mail works without them, but DMARC helps it reach inboxes and the SRV records let mail, calendar and contacts apps find the servers by themselves.",
        "optional": "Few apps use these yet; you can skip them."
      },
      "found": "found: {{values}}",
      "status": {
        "ok": "OK",
        "missing": "missing",
        "mismatch": "different"
      }
    },
    "external": {
      "title": "Registered elsewhere",
      "description": "Renewal, transfer lock and nameservers are managed at the registrar where you bought this domain."
    },
    "disconnect": {
      "title": "Disconnect",
      "description": "Removes the domain from this workspace and from the mail platform. Delete its mailboxes first.",
      "button": "Disconnect domain",
      "confirm": "Disconnect {{domain}}? Mail sent to it will no longer arrive here."
    }
  },
  "mail": {
    "noMailbox": "You do not have a mailbox yet.",
    "createInConsole": "Create mailboxes in the admin console",
    "askAdmin": "Ask a workspace administrator to create one for you.",
    "compose": "Compose",
    "newMessage": "New message",
    "to": "To",
    "cc": "Cc",
    "subject": "Subject",
    "send": "Send",
    "reply": "Reply",
    "flag": "Flag",
    "unflag": "Unflag",
    "markUnread": "Mark unread",
    "archive": "Archive",
    "delete": "Delete",
    "refresh": "Refresh",
    "empty": "Nothing here.",
    "pickOne": "Pick a message to read it.",
    "count_one": "{{count}} message",
    "count_other": "{{count}} messages",
    "folders": {
      "inbox": "Inbox",
      "sent": "Sent",
      "drafts": "Drafts",
      "trash": "Trash",
      "junk": "Junk",
      "archive": "Archive"
    },
    "search": "Search this folder",
    "loadOlder": "Load older messages",
    "images": "Load remote images",
    "imagesBlocked": "Remote images are blocked until you load them.",
    "attachments": "Attachments",
    "shortcuts": "Keys: j/k move · Enter open · r reply · c compose · e archive · # delete · s flag · u unread · / search · Esc close",
    "live": "live",
    "polling": "checking every 30 s",
    "summarize": "Summarize",
    "draftReply": "Draft reply",
    "aiInstructions": "Tell the assistant what the reply should say (optional)",
    "aiWorking": "Thinking…",
    "useDraft": "Use as reply",
    "aiRemaining_one": "{{count}} AI action left this month",
    "aiRemaining_other": "{{count}} AI actions left this month",
    "aiNote": "Only this message and your note are sent to the model. Check the result before you send it.",
    "back": "Back",
    "addCc": "Add Cc",
    "recipientSource": {
      "contact": "contact",
      "directory": "colleague",
      "recent": "recent"
    },
    "attach": "Attach",
    "dropFiles": "or drop files here",
    "uploading": "uploading…",
    "uploadFailed": "upload failed",
    "tooLarge": "larger than 50 MB",
    "removeAttachment": "Remove {{name}}",
    "draftSaving": "Saving draft…",
    "draftSaved": "Draft saved",
    "discard": "Discard",
    "sendingIn": "Sending in {{seconds}} s",
    "sentNow": "Message sent",
    "undo": "Undo",
    "tooLate": "Too late, it has left.",
    "notDeliveredTo": "Not delivered to: {{recipients}}",
    "notSentRefused": "Not sent: the mail server refused {{recipients}}. The message is still in Drafts.",
    "searchMail": "Search in mail",
    "clearSearch": "Clear search",
    "results": "Search results",
    "noResults": "No messages match your search.",
    "range": "{{from}}–{{to}} of {{total}}",
    "noSubject": "(no subject)",
    "noRecipient": "(no recipient)",
    "star": "Star",
    "unstar": "Remove star",
    "position": "{{position}} of {{total}}",
    "newer": "Newer",
    "older": "Older",
    "toLine": "to {{recipients}}",
    "attachmentCount_one": "{{count}} attachment",
    "attachmentCount_other": "{{count}} attachments",
    "forward": "Forward",
    "forwardedHeader": "Forwarded message",
    "from": "From",
    "date": "Date",
    "redraft": "Write again",
    "expand": "Expand",
    "minimize": "Minimize",
    "body": "Message",
    "recipientsPlaceholder": "Recipients",
    "needRecipient": "Add at least one recipient.",
    "invalidRecipient": "Not an email address: {{value}}. Separate addresses with commas.",
    "select": "Select",
    "selectOptions": "Select messages",
    "selectMessage": "Select: {{subject}}",
    "selectWhich": {
      "all": "All",
      "none": "None",
      "read": "Read",
      "unread": "Unread",
      "starred": "Starred",
      "unstarred": "Unstarred"
    },
    "selectedCount_one": "{{count}} selected",
    "selectedCount_other": "{{count}} selected",
    "clearSelection": "Clear selection",
    "markRead": "Mark read",
    "reportSpam": "Report spam",
    "notSpam": "Not spam",
    "moveTo": "Move to",
    "moveToInbox": "Move to inbox"
  },
  "calendar": {
    "today": "Today",
    "newEvent": "New event",
    "editEvent": "Edit event",
    "eventTitle": "Title",
    "calendar": "Calendar",
    "allDay": "All day",
    "date": "Date",
    "start": "Start",
    "end": "End",
    "location": "Location",
    "description": "Description",
    "delete": "Delete event",
    "untitled": "(untitled)",
    "create": "Create",
    "myCalendars": "My calendars",
    "previous": "Previous",
    "next": "Next",
    "view": "View",
    "views": {
      "day": "Day",
      "threeDays": "3 days",
      "week": "Week",
      "month": "Month",
      "schedule": "Schedule"
    },
    "addAllDay": "Add an all-day event",
    "more_one": "{{count}} more",
    "more_other": "{{count}} more",
    "nothingPlanned": "Nothing planned",
    "addTitle": "Add title",
    "addLocation": "Add location",
    "addDescription": "Add description"
  },
  "contacts": {
    "search": "Search contacts",
    "newContact": "New contact",
    "editContact": "Edit contact",
    "givenName": "First name",
    "familyName": "Last name",
    "organization": "Company",
    "emails": "Email addresses",
    "phones": "Phone numbers",
    "onePerLine": "One per line",
    "notes": "Notes",
    "delete": "Delete contact",
    "empty": "Your contacts will appear here.",
    "pickOne": "Pick a contact or create one.",
    "create": "Create contact",
    "all": "Contacts",
    "name": "Name",
    "email": "Email",
    "phone": "Phone",
    "noName": "(no name)",
    "noMatch": "No contacts match your search.",
    "edit": "Edit",
    "sendEmail": "Email",
    "call": "Call",
    "details": "Contact details",
    "noDetails": "No details yet.",
    "confirmDelete": "Delete {{name}}?",
    "addEmail": "Add email",
    "addPhone": "Add phone",
    "removeValue": "Remove",
    "other": "Other contacts",
    "otherHint": "People you have emailed are saved here, so their replies reach your inbox.",
    "otherEmpty": "People you email will appear here.",
    "addToContacts": "Add to contacts"
  },
  "store": {
    "headline": "Business email on your own domain.",
    "subline": "Mail, calendar, contacts and tasks for small businesses. Private, ad-free, and it works in every mail app. Try it free for 14 days, no card needed.",
    "openWorkspace": "Open workspace",
    "createWorkspace": "Create a workspace",
    "points": {
      "suite": {
        "title": "One workspace",
        "body": "Mail, calendar, contacts, tasks and notes on your domain, with open protocols for every phone and desktop app."
      },
      "private": {
        "title": "Private by design",
        "body": "No ads, no tracking, and your mail is never used to train AI. Export everything whenever you want."
      },
      "cost": {
        "title": "Honest pricing",
        "body": "A flat price per mailbox, no teaser rates, and you can cancel any time from your admin console."
      }
    }
  },
  "tasks": {
    "newList": "New list",
    "addPlaceholder": "Add a task and press Enter",
    "empty": "Nothing to do here.",
    "completed_one": "{{count}} completed",
    "completed_other": "{{count}} completed",
    "markDone": "Mark as done",
    "markOpen": "Mark as not done",
    "title": "Title",
    "important": "Important",
    "dueDate": "Due date",
    "dueTime": "Time",
    "list": "List",
    "notes": "Notes",
    "subtasks": "Subtasks",
    "addSubtask": "Add a subtask",
    "delete": "Delete task",
    "fromEmail": "From an email",
    "openEmail": "Open the email: {{subject}}",
    "addFromEmail": "Add as task",
    "added": "Added to tasks",
    "create": "Create",
    "sortBy": "Sort tasks",
    "sortManual": "My order",
    "sortDue": "Due date",
    "dragHint": "Drag to reorder",
    "moveUp": "Move up",
    "moveDown": "Move down",
    "reminder": "Reminder",
    "reminderNone": "No reminder",
    "reminderAtDue": "At the due time",
    "reminder15": "15 minutes before",
    "reminder60": "1 hour before",
    "reminder1440": "1 day before",
    "reminderDay9": "On the day at 09:00",
    "reminderDayBefore9": "The day before at 09:00",
    "reminderCustomBefore": "{{minutes}} minutes before",
    "reminderCustomAfter": "{{minutes}} minutes after",
    "reminderNeedsDue": "Set a due date to add a reminder.",
    "reminderWhere": "Rings in phone and desktop calendar apps, and here on devices with notifications on.",
    "notificationSettings": "Notification settings",
    "addTask": "Add a task",
    "starred": "Starred",
    "lists": "Lists",
    "newListName": "List name",
    "search": "Search tasks",
    "noMatch": "No tasks match your search.",
    "noStarred": "Star tasks to see them here.",
    "addDetails": "Add details",
    "clearDue": "Remove date"
  },
  "notes": {
    "all": "All notes",
    "notebooks": "Notebooks",
    "newNotebook": "New notebook",
    "tags": "Tags",
    "search": "Search notes",
    "newNote": "New note",
    "empty": "No notes here yet.",
    "pickOne": "Pick a note or create one.",
    "untitled": "Untitled",
    "pin": "Pin",
    "unpin": "Unpin",
    "color": "Colour",
    "colors": {
      "yellow": "Yellow",
      "green": "Green",
      "blue": "Blue",
      "purple": "Purple",
      "pink": "Pink",
      "gray": "Grey"
    },
    "notebook": "Notebook",
    "noNotebook": "No notebook",
    "saving": "Saving…",
    "title": "Title",
    "tagsPlaceholder": "Tags, separated by commas",
    "edit": "Write",
    "preview": "Read",
    "markdownHint": "Markdown: # heading, **bold**, - [ ] checklist",
    "export": "Export as Markdown",
    "delete": "Delete note",
    "confirmDelete": "Delete this note? This cannot be undone.",
    "saveFromEmail": "Save as note",
    "saved": "Saved to notes",
    "fromSender": "From {{from}}:",
    "sharedWithMe": "Shared with me",
    "share": "Share",
    "sharedWith_one": "Shared with {{count}}",
    "sharedWith_other": "Shared with {{count}}",
    "shareTitle": "People who can open this note",
    "searchPeople": "Add a colleague by name or email",
    "add": "Add",
    "remove": "Remove",
    "noPeople": "No colleague matches.",
    "canEdit": "can edit",
    "canView": "can view",
    "permission": "What {{name}} may do",
    "sharedBy": "Shared by {{name}}",
    "leave": "Leave",
    "conflict": "Someone else saved this note while you were typing. Your text is still here.",
    "conflictShort": "Not saved: changed elsewhere",
    "loadTheirs": "Show their version",
    "keepMine": "Keep my version",
    "notSaved": "Not saved",
    "takeNote": "Take a note…",
    "pinned": "Pinned",
    "others": "Others",
    "notebookName": "Notebook name",
    "noMatch": "No notes match your search.",
    "emptyNote": "Empty note",
    "noColor": "Default",
    "body": "Note"
  },
  "shell": {
    "menu": "Main menu",
    "closeMenu": "Close menu",
    "apps": "Apps",
    "account": "Account",
    "language": "Language",
    "theme": {
      "title": "Appearance",
      "light": "Light",
      "dark": "Dark",
      "system": "System"
    }
  },
  "devices": {
    "intro": "Read your mail, calendar and contacts in the apps on your phone or computer.",
    "password": {
      "title": "App password",
      "why": "Mail apps sign in with your email address and an app password, not your WebSede password.",
      "confirm": "Your WebSede password",
      "replaces": "A new app password replaces the old one: apps that used it will ask for the new one.",
      "create": "Create app password",
      "shownOnce": "Shown only once. Enter it in the app now; you can always create a new one."
    },
    "apple": {
      "title": "iPhone, iPad and Mac",
      "what": "One profile adds mail, calendar and contacts.",
      "step1": "Open this page in Safari on the device and tap Download profile.",
      "step2": "Open Settings, tap Profile Downloaded at the top, then Install.",
      "step3": "When asked for a password, enter the app password.",
      "download": "Download profile",
      "unverified": "The profile is not signed yet, so iOS shows it as Unverified. That is expected."
    },
    "android": {
      "title": "Android",
      "mail": "Mail in the Gmail app",
      "mail1": "In Gmail, tap your picture, then <b>Add another account</b> and <b>Other</b>.",
      "mail2": "Enter <b>{{address}}</b>, choose <b>Personal (IMAP)</b> and enter the app password.",
      "mail3": "Gmail fills in <b>{{domain}}</b> as the incoming server. Replace it with <b>{{host}}</b>; Gmail picks the port and security itself.",
      "mail4": "On the outgoing server screen Gmail fills in <b>{{domain}}</b> again: replace it with <b>{{host}}</b> too.",
      "outbox": "If sent mail stays in the Outbox, remove the account from Gmail and add it again.",
      "dav": "Calendar and contacts",
      "dav1": "Install <b>DAVx⁵</b> (free on F-Droid, paid on Google Play).",
      "dav2": "Choose <b>Login with URL and user name</b>: URL <b>{{url}}</b>, user name <b>{{address}}</b>, the app password.",
      "dav3": "Pick the calendars and address books to sync; they appear in the phone's own apps."
    },
    "manual": {
      "title": "Server settings",
      "auto": "Thunderbird and classic Outlook usually find these settings on their own: enter your address and the app password. If the app asks whether it may use setup information from {{host}}, allow it. Other apps need the settings below.",
      "username": "User name",
      "password": "Password",
      "passwordValue": "Your app password",
      "imap": "Incoming (IMAP)",
      "smtp": "Outgoing (SMTP)",
      "smtpAlt": "Outgoing, other port",
      "dav": "Calendar and contacts"
    }
  },
  "billing": {
    "yourPlan": "Your plan",
    "plans": "Plans",
    "monthly": "Monthly",
    "yearly": "Yearly · 2 months free",
    "perMailboxMonth": "per mailbox / month",
    "perMailboxYear": "per mailbox / year",
    "perMonth": "month",
    "perYear": "year",
    "seatsLine_one": "{{count}} mailbox × {{unit}} = {{total}} per {{period}}",
    "seatsLine_other": "{{count}} mailboxes × {{unit}} = {{total}} per {{period}}",
    "trialEnds_one": "Free trial ends {{date}} ({{count}} day left).",
    "trialEnds_other": "Free trial ends {{date}} ({{count}} days left).",
    "renewsOn": "Renews on {{date}}.",
    "endsOn": "Ends on {{date}}; it will not renew.",
    "manage": "Manage payment and invoices",
    "noCardYet": "No card on file yet. Choose a plan below; a running trial stays free until it ends.",
    "activating": "Thanks! Your plan is being activated; this takes a few seconds.",
    "current": "Current plan",
    "choose": "Choose {{plan}}",
    "switchTo": "Switch to {{plan}}",
    "confirmSwitch": "Switch plans now? The price difference is prorated on your next invoice.",
    "footnote": "Prices in US dollars per mailbox. Sales tax may apply. Cancel anytime under Manage payment and invoices.",
    "notesNotInPlan": "Notes are part of the Team plan. Your notes are kept and you can still read them.",
    "access": {
      "trial": "Free trial",
      "active": "Active",
      "past_due": "Payment failed",
      "expired": "Paused"
    },
    "banner": {
      "trialEnds_one": "Your free trial ends in {{count}} day.",
      "trialEnds_other": "Your free trial ends in {{count}} days.",
      "cardNeeded_one": "Add a card to keep sending outside your workspace and from phone or desktop mail apps. Trial ends in {{count}} day.",
      "cardNeeded_other": "Add a card to keep sending outside your workspace and from phone or desktop mail apps. Trial ends in {{count}} days.",
      "pastDue": "Your last payment failed. Update your card to keep your workspace running.",
      "expired": "Your workspace is paused: mail still arrives, but sending needs a plan.",
      "choose": "Choose a plan",
      "fix": "Update card",
      "askAdmin": "Ask a workspace administrator.",
      "sendingPaused": "Sending to outside addresses is paused because of bounces or spam complaints. Incoming mail still arrives and you can still send to people inside the workspace from the web app; phone and desktop mail apps cannot send until the pause is lifted. Write to {{support}} to turn it back on."
    },
    "plan": {
      "personal": {
        "name": "Personal",
        "tagline": "Professional email on your own domain.",
        "features": [
          "Email with 10 GB per mailbox",
          "Calendar and contacts",
          "Tasks",
          "Works with phone and desktop mail apps"
        ]
      },
      "team": {
        "name": "Team",
        "tagline": "For small teams that work together.",
        "features": [
          "Everything in Personal",
          "Email with 25 GB per mailbox",
          "Notes, shared with your team"
        ]
      }
    },
    "firstCharge": "Free until {{date}}; the first charge is on that day.",
    "yearlyOnly": "Billed yearly only, {{amount}} a month",
    "complimentary": {
      "title": "Complimentary workspace",
      "body": "This workspace has the Team plan's features and is never billed. Mailboxes can be added freely."
    }
  },
  "public": {
    "nav": {
      "pricing": "Pricing",
      "startTrial": "Start free trial",
      "comingSoon": "Coming soon"
    },
    "contact": "Contact",
    "home": {
      "seePricing": "See pricing"
    },
    "legal": {
      "terms": "Terms",
      "privacy": "Privacy",
      "refunds": "Refunds",
      "acceptableUse": "Acceptable use",
      "draft": "Draft: this document is being reviewed and is not in effect yet.",
      "notFound": "This page does not exist."
    },
    "pricing": {
      "title": "Simple pricing per mailbox",
      "subtitle": "Every plan includes your own domain, secure sign-in and the apps you already use. Pay monthly, or yearly and get two months free.",
      "trialNote": "Every workspace starts with a 14-day free trial of Team. No card needed to start.",
      "faqTitle": "Questions",
      "faq": {
        "trial": {
          "q": "How does the free trial work?",
          "a": "You get 14 days of the Team plan with up to 5 mailboxes. Add a card during the first 3 days to keep emailing people outside your workspace; you are charged only when the trial ends."
        },
        "card": {
          "q": "Which payment methods do you accept?",
          "a": "All major credit and debit cards, processed securely by Stripe. We never see or store your card number."
        },
        "cancel": {
          "q": "Can I cancel any time?",
          "a": "Yes. Cancel from Billing in your admin console; your plan runs until the end of the period you paid for. Your first payment is covered by a 30-day money-back guarantee."
        },
        "tax": {
          "q": "Are taxes included?",
          "a": "Prices are in US dollars and exclude sales tax, which is added where the law requires it."
        },
        "domain": {
          "q": "Do I need my own domain?",
          "a": "Yes. Connect a domain you already own; we show you the DNS records to add and check them for you."
        }
      }
    }
  },
  "dataControls": {
    "account": {
      "title": "Your data",
      "body": "Download your profile, notes, calendars, contacts and tasks as a JSON file. Messages stay in your mail apps: export them over IMAP.",
      "download": "Download my data"
    },
    "workspace": {
      "title": "Workspace data",
      "body": "Download the workspace's records: members, domains, mailboxes, billing and the audit log, as a JSON file. Each member downloads their own notes, calendars and contacts in Settings.",
      "download": "Export workspace data"
    },
    "close": {
      "title": "Close the workspace",
      "body": "Closing {{name}} ends the subscription now, signs everyone out for good and stops sending. Export what you need first. Mailboxes stay readable in mail apps (IMAP) for 30 days, then everything is deleted. Contact support within those 30 days to reopen it.",
      "name": "Type the workspace name ({{name}}) to confirm",
      "password": "Your password",
      "submit": "Close the workspace",
      "confirm": "Close the workspace now? Everyone is signed out immediately.",
      "done": "The workspace is closed. Its data will be deleted on {{date}}. You are being signed out."
    }
  }
}
